import React from 'react'
import { Button } from '../ui/button'
import {
  Container,
  Truck,
  Download,
  ChevronDown,
  Ship,
  AlertCircle,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../ui/tooltip'

interface DispatchHeaderProps {
  totalCount: number
  isRealtimeConnected: boolean
  lastUpdateTime?: Date | null
  onDispatchTask: () => void
  onExportCSV?: () => void
  onExportExcel?: () => void
  onContainerMonitor?: () => void
  monitorUrgentCount?: number
  /** Opens the failed-operations centre. */
  onOperationFailures?: () => void
  /** Open failures needing attention — drives the red bubble. */
  failureErrorCount?: number
  /** Open failures that are degraded but self-healing — amber, no bubble. */
  failureWarningCount?: number
}

export const DispatchHeader: React.FC<DispatchHeaderProps> = ({
  totalCount,
  isRealtimeConnected,
  lastUpdateTime,
  onDispatchTask,
  onExportCSV,
  onExportExcel,
  onContainerMonitor,
  monitorUrgentCount = 0,
  onOperationFailures,
  failureErrorCount = 0,
  failureWarningCount = 0,
}) => {
  const hasFailures = failureErrorCount > 0 || failureWarningCount > 0
  const failureTitle = hasFailures
    ? `${failureErrorCount} failure(s) need attention` +
      (failureWarningCount > 0 ? `, ${failureWarningCount} warning(s)` : '')
    : 'Failed operations — nothing outstanding'

  return (
    <div className="flex items-center justify-between mb-3">
      {/* Left: Title + count + connection dot */}
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-[#00838F] rounded-lg flex items-center justify-center">
          <Container className="w-4.5 h-4.5 text-white" />
        </div>
        <h1 className="text-lg font-bold text-slate-900">Container Management</h1>
        <span className="text-sm text-slate-500 font-medium">{totalCount}</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1.5 cursor-default">
              <div
                className={`w-2 h-2 rounded-full ${
                  isRealtimeConnected
                    ? 'bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.6)] animate-pulse'
                    : 'bg-amber-400 animate-pulse'
                }`}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            {isRealtimeConnected ? 'Real-time connected (WebSocket)' : 'Auto-refresh (polling)'}
            {lastUpdateTime && (
              <span className="block text-slate-400 mt-0.5">
                Last update: {lastUpdateTime.toLocaleTimeString()}
              </span>
            )}
          </TooltipContent>
        </Tooltip>
      </div>

      {/* Right: Action buttons */}
      <div className="flex items-center gap-2">
        {/* Failed-operations centre. Sits first so it reads before the actions
            beside it — if something has silently failed, that is the thing the
            dispatcher needs to see before starting anything new. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={onOperationFailures}
              size="sm"
              variant="outline"
              aria-label={failureTitle}
              className={`h-8 w-8 p-0 relative ${
                failureErrorCount > 0
                  ? 'border-red-300 text-red-600 hover:bg-red-50'
                  : failureWarningCount > 0
                    ? 'border-amber-300 text-amber-600 hover:bg-amber-50'
                    : 'border-slate-300 text-slate-400'
              }`}
            >
              <AlertCircle className="w-4 h-4" />
              {failureErrorCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full px-1">
                  {failureErrorCount > 99 ? '99+' : failureErrorCount}
                </span>
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <span className="text-xs">{failureTitle}</span>
          </TooltipContent>
        </Tooltip>
        <Button
          onClick={onContainerMonitor}
          size="sm"
          variant="outline"
          className="h-8 text-xs border-slate-300 relative"
        >
          <Ship className="w-3.5 h-3.5 mr-1.5" />
          Container Monitor
          {monitorUrgentCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full px-1 animate-pulse">
              {monitorUrgentCount}
            </span>
          )}
        </Button>
        <Button
          onClick={onDispatchTask}
          size="sm"
          className="bg-[#00838F] hover:bg-[#006064] text-white h-8 text-xs"
        >
          <Truck className="w-3.5 h-3.5 mr-1.5" />
          Dispatch Task
        </Button>
        {/* Export dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 text-xs border-slate-300">
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Export
              <ChevronDown className="w-3 h-3 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onExportCSV}>Export CSV</DropdownMenuItem>
            <DropdownMenuItem onClick={onExportExcel}>Export Excel</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

      </div>
    </div>
  )
}
